import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExpandedCategories } from "../../../../../types/type";
import { FilterSkeleton } from "../../../../../shared/ui";
import { QueryErrorFallback } from "../../../../../shared/ui";
import { useFilterDataQuery } from "../../config/useFilterDataQuery";
import CategorySection from "./CategorySection";
import { useLanguage } from "../../../../../shared/hooks/useLanguageContext";
import {
  createMultilingualKeyword,
  createMultilingualProduct,
} from "../../../../../shared/utils/multilingual-helpers";
import { useTranslation } from "../../../../../shared/hooks/useTranslation";

type KeywordFilterProps = {
  onReset: boolean;
  setOnResetTriggered: (state: boolean) => void;
  isMobile: boolean;
  isMobileMenuOpen: boolean;
  closeMobileMenu: () => void;
};

export default function KeywordFilter({
  onReset,
  setOnResetTriggered,
  isMobile,
  isMobileMenuOpen,
  closeMobileMenu,
}: KeywordFilterProps) {
  const filterDataQuery = useFilterDataQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedCategories, setExpandedCategories] =
    useState<ExpandedCategories>({});
  const [isExpandedProduct, setIsExpandedProduct] = useState(false);

  // 다국어 지원을 위한 훅
  const { getLocalizedText } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    if (onReset) {
      setSearchParams(new URLSearchParams());
      setOnResetTriggered(false);
    }
  }, [onReset, setSearchParams, setOnResetTriggered]);

  const toggleExpandCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleExpandProduct = () => {
    setIsExpandedProduct((prev) => !prev);
  };

  const handleFilterChange = (
    filterType: "keyword" | "product" | "uxEvaluation",
    id: string,
    checked: boolean,
  ) => {
    const paramName = `${filterType}_${id}`;

    if (checked) {
      searchParams.append(filterType, paramName);
    } else {
      const paramsToRemove = searchParams.getAll(filterType);
      searchParams.delete(filterType);

      paramsToRemove.forEach((param) => {
        if (param !== paramName) {
          searchParams.append(filterType, param);
        }
      });
    }

    setSearchParams(searchParams);
  };

  const handleDivClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById(id)?.click();
  };

  return (
    <div className="bottom-left-view">
      <div
        className={`keyword-filter ${isMobile ? "mobile" : ""} ${isMobileMenuOpen ? "open" : ""}`}
      >
        <div className="menu-title">
          <div className="title-text">{t("filter.keywordFiltering")}</div>
          <div className="done-btn" onClick={closeMobileMenu}>
            {t("filter.done")}
          </div>
        </div>
        {isMobile && isMobileMenuOpen && (
          <div className="for-mobile-padding-top"></div>
        )}

        {filterDataQuery.isLoading ? (
          <FilterSkeleton isMobile={isMobile} />
        ) : filterDataQuery.isError ? (
          <QueryErrorFallback
            error={new Error(t("filter.loadFilterDataError"))}
            title={t("filter.loadFilterDataError")}
            description={t("filter.loadFilterDataErrorDesc")}
          />
        ) : (
          <>
            {filterDataQuery.keywordsQuery.data &&
              filterDataQuery.productsQuery.data &&
              filterDataQuery.uxEvaluationsQuery.data && (
                <>
                  {/* UX 분석 필터 */}
                  <CategorySection
                    title={t("filter.uxAnalysis")}
                    items={
                      filterDataQuery.uxEvaluationsQuery.data
                        ? [
                            {
                              id: "GOOD",
                              name: "Good UX",
                              count:
                                filterDataQuery.uxEvaluationsQuery.data
                                  .goodUxCount || 0,
                            },
                            {
                              id: "BAD",
                              name: "Bad UX",
                              count:
                                filterDataQuery.uxEvaluationsQuery.data
                                  .badUxCount || 0,
                            },
                          ]
                        : []
                    }
                    filterType="uxEvaluation"
                    expanded={false}
                    handleFilterChange={handleFilterChange}
                    handleDivClick={handleDivClick}
                  />

                  {/* 프로덕트 필터 */}
                  <CategorySection
                    title={t("filter.product")}
                    items={
                      filterDataQuery.productsQuery.data &&
                      Array.isArray(filterDataQuery.productsQuery.data)
                        ? filterDataQuery.productsQuery.data
                            .sort(
                              (a: any, b: any) =>
                                b.numOfArticles - a.numOfArticles,
                            )
                            .map((el: any) => {
                              const multilingualProduct =
                                createMultilingualProduct({
                                  nameKo: el.nameKo,
                                  nameEn: el.nameEn,
                                });
                              return {
                                id: el.id,
                                name: getLocalizedText(
                                  multilingualProduct.name,
                                ),
                                count: el.numOfArticles,
                              };
                            })
                        : []
                    }
                    filterType="product"
                    expanded={isExpandedProduct}
                    toggleExpand={toggleExpandProduct}
                    handleFilterChange={handleFilterChange}
                    handleDivClick={handleDivClick}
                  />

                  {/* UX 키워드 필터 */}
                  {filterDataQuery.keywordsQuery.data &&
                    Array.isArray(filterDataQuery.keywordsQuery.data) &&
                    filterDataQuery.keywordsQuery.data.map((category: any) => {
                      const sortedKeywords = [...category.keywords].sort(
                        (a: any, b: any) => (a.nameKo > b.nameKo ? 1 : -1),
                      );

                      // 카테고리명 다국어 처리
                      const multilingualCategory = createMultilingualKeyword({
                        nameKo: category.categoryNameKo,
                        nameEn: category.categoryNameEn,
                        descKo: "",
                        descEn: "",
                        categoryNameKo: category.categoryNameKo,
                        categoryNameEn: category.categoryNameEn,
                      });

                      return (
                        <CategorySection
                          key={category.categoryId}
                          title={getLocalizedText(
                            multilingualCategory.categoryName,
                          )}
                          items={sortedKeywords
                            .sort(
                              (a: any, b: any) =>
                                b.numOfArticles - a.numOfArticles,
                            )
                            .map((kw: any) => {
                              const multilingualKeyword =
                                createMultilingualKeyword({
                                  nameKo: kw.nameKo,
                                  nameEn: kw.nameEn,
                                  descKo: kw.descKo,
                                  descEn: kw.descEn,
                                  categoryNameKo: category.categoryNameKo,
                                  categoryNameEn: category.categoryNameEn,
                                });
                              return {
                                id: kw.id,
                                name: getLocalizedText(
                                  multilingualKeyword.name,
                                ),
                                count: kw.numOfArticles,
                              };
                            })}
                          filterType="keyword"
                          keywordUniqueId={`keyword-${category.categoryId}`}
                          expanded={
                            expandedCategories[category.categoryId!] || false
                          }
                          toggleExpand={() =>
                            toggleExpandCategory(category.categoryId!)
                          }
                          handleFilterChange={handleFilterChange}
                          handleDivClick={handleDivClick}
                        />
                      );
                    })}
                </>
              )}
          </>
        )}
      </div>
    </div>
  );
}
